import React, { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';

const shadowStyle = {
  visibility: 'hidden', position: 'absolute', overflow: 'hidden', height: 0,
  top: 0, left: 0, paddingTop: 0, paddingBottom: 0,
};

function resizeTextarea(input, shadow, minRows, maxRows, placeholder) {
  const computed = input.ownerDocument.defaultView.getComputedStyle(input);
  if (computed.width === '0px') return;
  shadow.style.width = computed.width;
  shadow.value = input.value || placeholder || 'x';
  // An empty final line must still occupy a row in the measuring textarea.
  if (shadow.value.endsWith('\n')) shadow.value += ' ';
  const contentHeight = shadow.scrollHeight;
  shadow.value = 'x';
  const rowHeight = shadow.scrollHeight;
  if (!rowHeight) return;
  const height = Math.max(rowHeight * minRows, Math.min(contentHeight, rowHeight * maxRows));
  const px = (name) => parseFloat(computed[name]) || 0;
  const extra = computed.boxSizing === 'border-box'
    ? px('paddingTop') + px('paddingBottom') + px('borderTopWidth') + px('borderBottomWidth') : 0;
  const nextHeight = `${height + extra}px`;
  if (input.style.height !== nextHeight) input.style.height = nextHeight;
  const overflow = contentHeight > height + 1 ? 'auto' : 'hidden';
  if (input.style.overflowY !== overflow) input.style.overflowY = overflow;
}

/** Composer's MUI input slot: all autosizing runs outside ResizeObserver delivery. */
const ComposerTextarea = forwardRef(function ComposerTextarea({
  minRows = 1, maxRows = 6, className, style, placeholder,
  ownerState, type, as, rows, ...other
}, forwardedRef) {
  const inputRef = useRef(null);
  const shadowRef = useRef(null);
  const measureRef = useRef(null);
  const scheduleRef = useRef(null);
  const setRef = useCallback((node) => {
    inputRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    const win = input.ownerDocument.defaultView;
    let frame = null;
    let closed = false;
    const schedule = () => {
      if (closed || frame !== null) return;
      frame = win.requestAnimationFrame(() => {
        frame = null;
        if (!closed) measureRef.current?.();
      });
    };
    scheduleRef.current = schedule;
    const observer = typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(schedule) : null;
    observer?.observe(input);
    win.addEventListener('resize', schedule);
    return () => {
      closed = true;
      scheduleRef.current = null;
      observer?.disconnect();
      win.removeEventListener('resize', schedule);
      if (frame !== null) win.cancelAnimationFrame(frame);
    };
  }, []);

  // Controlled edits, clears and theme changes share the same pending frame.
  useLayoutEffect(() => {
    measureRef.current = () => resizeTextarea(inputRef.current, shadowRef.current, minRows, maxRows, placeholder);
    scheduleRef.current?.();
  });

  return (
    <>
      <textarea {...other} ref={setRef} rows={rows || minRows} className={className} style={style} placeholder={placeholder} />
      <textarea ref={shadowRef} className={className} aria-hidden="true" readOnly tabIndex={-1} style={{ ...style, ...shadowStyle }} />
    </>
  );
});

export default ComposerTextarea;
